import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import PageHeader from "../../../components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import AddEditFeeModal from "../modals/AddEditFeeModal";
import EditFeeAmountModal from "../modals/EditFeeAmountModal";
import StudentFeesTable from "../table/StudentFeesTable";
import useFee from "../hooks/useFee";
import { FilterField } from "../../../components/Common/TableFilter/TableFilter";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import { formatToSQLDate } from "utils/dateUtils";
import dayjs from "dayjs";
import { useAbility } from "config/abilityContext";
import useClassManagement from "feature-module/academic/classes/hooks/useClassManagement";
import useReference from "hooks/useReference";

const FinanceFees = () => {
  const { t } = useTranslation();
  const ability = useAbility();

  const {
    fees,
    infoPagination,
    loading,
    selectedFee,
    setSelectedFee,
    isModalOpen,
    setIsModalOpen,
    fetchFees,
    handleCreate,
    handleCreateByClassId,
    handleDelete,
    fetchStudents,
    fetchFeeTypes,
    students,
    feeTypes,
    statusFee,
    handleUpdate,
  } = useFee();

  const {fetchClassLevels , classLevels} = useReference()

  const { classes, fetchClasses } = useClassManagement();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const breadcrumbItems = [
    { label: "finance_dashboard", path: all_routes.financeDashboard },
    { label: "fees", active: true },
  ];

  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>(() => {
    const start = formatToSQLDate(dayjs().startOf("month"));
    const end = formatToSQLDate(dayjs().endOf("month"));
    return { dueDateStart: start, dueDateEnd: end };
  });

  useEffect(() => {
    handleFilter(filters);
    fetchStudents();
    fetchFeeTypes();
    fetchClasses();
    fetchClassLevels()
  }, []);

  const RefreshData = () => {
    fetchFees({
      page: infoPagination.page || 1,
      limit: infoPagination.limit || 10,
      ...filters,
    });
  };

  const RefreshToFirstPage = () => {
    fetchFees({
      page: 1,
      limit: 10,
      ...filters,
    });
  };

  const filterFields: FilterField[] = [
    { key: "search", label: t("search"), type: "text" },
    {
      key: "studentId",
      label: t("student_name"),
      type: "select",
      options: students.map((student) => ({
        label: `${student.user.firstName} ${student.user.lastName}`,
        value: student.id,
      })),
    },
    {
      key: "feeTypeId",
      label: t("fee_type"),
      type: "select",
      options: feeTypes.map((feeType) => ({
        label: feeType.name,
        value: feeType.id,
      })),
    },
    {
      key: "status",
      label: t("status"),
      type: "select",
      options: statusFee,
    },
    {
      key: "dueDate",
      label: t("due_date"),
      type: "range",
      rangeKeys: { start: "dueDateStart", end: "dueDateEnd" },
    },
  ];

  const handleFilter = (newFilters: { [key: string]: string | number | null }) => {
    setFilters(newFilters);
    fetchFees({
      page: infoPagination.page || 1,
      limit: infoPagination.limit || 10,
      ...newFilters,
    });
  };

  const handleSaveFee = async (feeData: any) => {
    if (feeData.studentIds) {
      const result = await handleCreateByClassId(feeData);
      if (result) {
        setIsModalOpen(false);
        RefreshToFirstPage();
      }
      return result;
    } else {
      const result = await handleCreate(feeData);
      if (result) {
        setIsModalOpen(false);
        RefreshToFirstPage();
      }
      return result;
    }
  };

  const handleEdit = (record: any) => {
    setSelectedFee(record);
    setIsEditModalOpen(true);
  };

  const handleUpdateFeeAmount = async (amount: number) => {
    if (!selectedFee) return false;

    const result = await handleUpdate(selectedFee.id, { amount });
    if (result) {
      setIsEditModalOpen(false);
      setSelectedFee(null);
      RefreshData();
      return true;
    }
    return false;
  };

  const handleDeleteFee = async (id: string) => {
    const result = await handleDelete(id);
    if (result) {
      RefreshData();
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("fees")} breadcrumbItems={breadcrumbItems} />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Fee") && (
            <CustomButton
              onClick={() => {
                setSelectedFee(null);
                setIsModalOpen(true);
              }}
            >
              <i className="ti ti-plus me-2" />
              {t("add_fees")}
            </CustomButton>
          )}

          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={(newFilters) => handleFilter(newFilters)}
            onReset={() => {
              setFilters({});
              fetchFees({ page: 1, limit: 10 });
            }}
          />
        </div>

        <StudentFeesTable
          dataSource={fees}
          onEdit={handleEdit}
          onDelete={handleDeleteFee}
          loading={loading}
          dataPaginate={{
            current: infoPagination?.page || 1,
            pageSize: infoPagination?.limit || 10,
            total: infoPagination?.total,
          }}
          onChange={(pg) => {
            fetchFees({
              ...filters,
              page: pg.current || 1,
              limit: pg.pageSize || 10,
            });
          }}
        />

        {/* Add Fee Modal */}
        <AddEditFeeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveFee}
          students={students}
          feeTypes={feeTypes}
          classes={classes}
          classLevels={classLevels}
        />

        <EditFeeAmountModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedFee(null);
          }}
          currentAmount={Number(selectedFee?.amount || 0)}
          onSave={handleUpdateFeeAmount}
        />
      </div>
    </div>
  );
};

export default FinanceFees;

